import { useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import * as draftLetterService from '../../services/caseRequestDraftLetter';
import { setFlashToast } from '../../utils/flashToast';
import { printTextDocument } from '../../utils/printHtml';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';

type Props = {
  caseRequestId: string;
  readOnly?: boolean;
};

function findTemplate(
  templates: draftLetterService.CaseRequestLetterTemplateContent[],
  templateId: string,
): draftLetterService.CaseRequestLetterTemplateContent | null {
  const id = templateId.trim();
  if (!id) return null;
  return templates.find((tpl) => tpl.id === id) ?? null;
}

export default function TaskCaseDraftLetterTab({
  caseRequestId,
  readOnly = false,
}: Props) {
  const canLoad = isCaseRequestUuid(caseRequestId);
  const [loading, setLoading] = useState(canLoad);
  const [loadError, setLoadError] = useState('');
  const [templates, setTemplates] = useState<draftLetterService.CaseRequestLetterTemplateContent[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [defaultBody, setDefaultBody] = useState('');
  const [draftLetterId, setDraftLetterId] = useState('');

  const [body, setBody] = useState('');
  const [savedBody, setSavedBody] = useState('');

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [switchTemplateConfirmOpen, setSwitchTemplateConfirmOpen] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState('');

  const selectedTemplate = useMemo(
    () => findTemplate(templates, selectedTemplateId),
    [templates, selectedTemplateId],
  );
  const templateName = useMemo(() => selectedTemplate?.name?.trim() || '—', [selectedTemplate?.name]);

  const applyTemplate = useCallback((tpl: draftLetterService.CaseRequestLetterTemplateContent) => {
    const initial = tpl.letterContent ?? '';
    setSelectedTemplateId(tpl.id);
    setDefaultBody(initial);
    setBody(initial);
    setSavedBody(initial);
    setDraftLetterId('');
    setEditing(false);
  }, []);

  const load = useCallback(async () => {
    if (!canLoad) {
      setLoading(false);
      setLoadError('Case request id is missing or invalid.');
      return;
    }

    setLoading(true);
    setLoadError('');
    const r = await draftLetterService.getCaseRequestLetterTemplatesContent(caseRequestId);
    if (!r.success) {
      setLoading(false);
      if (draftLetterService.isNoLetterTemplatesError(r.message ?? '')) {
        setLoadError(draftLetterService.DRAFT_LETTER_NO_TEMPLATES_MESSAGE);
        return;
      }
      const msg = r.message ?? 'Failed to load letter templates.';
      setLoadError(msg);
      setFlashToast({ message: msg, variant: 'error' });
      return;
    }

    setTemplates(r.data.templates);

    const draftRes = await draftLetterService.getCaseRequestDraftLetter(caseRequestId);
    setLoading(false);

    if (draftRes.success && draftRes.data) {
      const draft = draftRes.data;
      const tpl =
        findTemplate(r.data.templates, draft.letterTemplateId) ?? r.data.templates[0];
      setDraftLetterId(draft.id);
      setSelectedTemplateId(tpl.id);
      setDefaultBody(tpl.letterContent ?? '');
      setBody(draft.content);
      setSavedBody(draft.content);
      setEditing(false);
      return;
    }

    applyTemplate(r.data.templates[0]);
  }, [applyTemplate, canLoad, caseRequestId]);

  useEffect(() => {
    void load();
  }, [load]);

  const canEdit = !readOnly;
  const canAct = canEdit && !loading && !saving;

  const hasAnyChanges = body !== defaultBody;
  const hasUnsavedChanges = body !== savedBody;
  const canSubmitLetter = canAct && Boolean(body.trim());
  const canUpdateLetter = canAct && Boolean(body.trim()) && (editing ? hasUnsavedChanges : true);

  const beginCancel = () => {
    if (!canEdit || !editing) return;
    if (body !== savedBody) {
      setCancelConfirmOpen(true);
      return;
    }
    setEditing(false);
  };

  const beginUpdate = () => {
    if (!canEdit) return;
    if (!editing) {
      setEditing(true);
      return;
    }
    void save();
  };

  const beginReset = () => {
    if (!canEdit || !editing) return;
    if (!hasAnyChanges) return;
    setResetConfirmOpen(true);
  };

  const switchTemplate = (templateId: string) => {
    const tpl = findTemplate(templates, templateId);
    if (!tpl || tpl.id === selectedTemplateId) return;
    applyTemplate(tpl);
    setPendingTemplateId('');
    setSwitchTemplateConfirmOpen(false);
  };

  const beginTemplateChange = (templateId: string) => {
    if (!canEdit || templateId === selectedTemplateId) return;
    if (editing && hasUnsavedChanges) {
      setPendingTemplateId(templateId);
      setSwitchTemplateConfirmOpen(true);
      return;
    }
    switchTemplate(templateId);
  };

  const save = async (exitEditMode = false) => {
    if (!canLoad || !canEdit) return;
    if (!selectedTemplate?.id?.trim()) {
      setFlashToast({ message: 'Letter template is missing.', variant: 'error' });
      return;
    }
    if (!body.trim()) {
      setFlashToast({ message: 'Letter body is required.', variant: 'error' });
      return;
    }
    if (saving || !hasUnsavedChanges) return;

    setSaving(true);
    const r = await draftLetterService.createCaseRequestDraftLetter({
      caseRequestId,
      letterTemplateId: selectedTemplate.id,
      content: body,
      draftLetterId: draftLetterId || undefined,
    });
    setSaving(false);
    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Could not update draft letter.', variant: 'error' });
      return;
    }

    setDraftLetterId(r.data.id);
    setSavedBody(body);
    if (exitEditMode) setEditing(false);
    setFlashToast({ message: 'Draft letter updated successfully.', variant: 'success' });
  };

  if (!canLoad) {
    return <p className="tcd-doc-empty-msg">Case request id is missing or invalid.</p>;
  }

  if (loading) {
    return <p className="tcd-loading">Loading draft letter…</p>;
  }

  if (loadError) {
    if (loadError === draftLetterService.DRAFT_LETTER_NO_TEMPLATES_MESSAGE) {
      return <p className="tcd-doc-empty-msg">{loadError}</p>;
    }
    return <p className="tcd-error" role="alert">{loadError}</p>;
  }

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setBody(savedBody);
          setEditing(false);
          setCancelConfirmOpen(false);
        }}
      />
      <ConfirmDialog
        isOpen={resetConfirmOpen}
        title="Reset Letter"
        message="Reset the letter body to the template default?"
        onClose={() => setResetConfirmOpen(false)}
        onConfirm={() => {
          setBody(defaultBody);
          setResetConfirmOpen(false);
        }}
      />
      <ConfirmDialog
        isOpen={switchTemplateConfirmOpen}
        title="Switch Letter Template"
        message="Switching templates will discard unsaved changes. Continue?"
        onClose={() => {
          setPendingTemplateId('');
          setSwitchTemplateConfirmOpen(false);
        }}
        onConfirm={() => switchTemplate(pendingTemplateId)}
      />

      <div className="tcd-letter">
        <h3 className="tcd-letter-title">Draft a Letter</h3>

        <div className="tcd-letter-template-row">
          <div className="ccr-fg tcd-letter-template-field">
            <label htmlFor="tcd-letter-template">Letter Template</label>
            <select
              id="tcd-letter-template"
              className="ccr-inp"
              value={selectedTemplateId}
              disabled={!canEdit || saving || templates.length <= 1}
              onChange={(e) => beginTemplateChange(e.target.value)}
            >
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn-ca tcd-letter-print-btn"
            disabled={!body.trim()}
            onClick={() => printTextDocument(templateName, body)}
          >
            Print
          </button>
        </div>

        <div className="tcd-letter-body-section">
          <div className="tcd-letter-body-hdr">
            <label className="tcd-letter-body-label" htmlFor="tcd-letter-body">
              Letter Body
            </label>
            {canEdit ? (
              <div className="tcd-letter-inline-actions">
                <button
                  type="button"
                  className="tcd-letter-btn-neutral"
                  disabled={!canAct || !editing || !hasAnyChanges}
                  onClick={beginReset}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn-ca"
                  disabled={!canUpdateLetter}
                  onClick={beginUpdate}
                >
                  {saving ? 'Updating…' : 'Update'}
                </button>
              </div>
            ) : null}
          </div>
          {editing ? (
            <textarea
              id="tcd-letter-body"
              className="note-ta tcd-letter-ta"
              rows={16}
              value={body}
              disabled={saving}
              onChange={(e) => setBody(e.target.value)}
            />
          ) : (
            <pre className="tcd-letter-preview" id="tcd-letter-body">
              {body.trim() || 'No data found'}
            </pre>
          )}
        </div>

        {canEdit && editing ? (
          <div className="tcd-letter-footer">
            <button type="button" className="tcd-letter-btn-neutral tcd-letter-footer-btn" disabled={saving} onClick={beginCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-ca tcd-letter-footer-btn"
              disabled={saving || !canSubmitLetter || !hasUnsavedChanges}
              onClick={() => void save(true)}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
